import React, { useState } from 'react';
import axios from 'axios';
import config from '../config';
import { showToast } from './Toast';
import './styles/public-tracking.css';

export default function PublicTracking() {
  const [trackNumber, setTrackNumber] = useState('');
  const [phone, setPhone] = useState('');
  const [track, setTrack] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedHistory, setExpandedHistory] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    
    if (!trackNumber.trim()) {
      setError('Пожалуйста, введите номер трека');
      return;
    }

    setLoading(true);
    setError(null);
    setTrack(null);

    try {
      const response = await axios.get(`${config.apiUrl}/api/track/public/search`, {
        params: {
          trackNumber: trackNumber.trim(),
          ...(phone && { phone })
        }
      });

      if (response.data.success) {
        setTrack(response.data.track);
        showToast('Трек найден!', 'success');
      } else {
        setError(response.data.message || 'Трек не найден');
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setError('Трек с таким номером не найден. Пожалуйста, проверьте номер.');
      } else {
        setError('Ошибка при поиске трека. Пожалуйста, попробуйте позже.');
        console.error('Search error:', err);
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('ru-RU');
  };

  const getStatusColor = (statusText) => {
    const statusLower = (statusText || '').toLowerCase();
    if (statusLower.includes('получено')) return '#4CAF50';
    if (statusLower.includes('доставка')) return '#2196F3';
    if (statusLower.includes('склад')) return '#FF9800';
    if (statusLower.includes('ошибка') || statusLower.includes('проблема')) return '#F44336';
    return '#9C27B0';
  };

  return (
    <div className="public-tracking-container">
      <div className="tracking-card">
        <div className="tracking-header">
          <h1>🔍 Отследить посылку</h1>
          <p>Введите номер вашей посылки для отслеживания</p>
        </div>

        <form onSubmit={handleSearch} className="tracking-form">
          <div className="form-group">
            <label htmlFor="trackNumber">Номер посылки</label>
            <input
              id="trackNumber"
              type="text"
              placeholder="Например: ABC123456"
              value={trackNumber}
              onChange={(e) => setTrackNumber(e.target.value)}
              className="form-input"
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="phone">Номер телефона (опционально)</label>
            <input
              id="phone"
              type="tel"
              placeholder="87012345678"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="form-input"
              disabled={loading}
            />
            <small>Укажите телефон для более точного поиска</small>
          </div>

          <button
            type="submit"
            className={`btn-search ${loading ? 'loading' : ''}`}
            disabled={loading}
          >
            {loading ? '⏳ Поиск...' : '🔍 Найти трек'}
          </button>
        </form>

        {error && (
          <div className="error-message">
            <p>❌ {error}</p>
          </div>
        )}

        {track && (
          <div className="track-result">
            <div className="track-header-result">
              <h2>📦 Номер посылки: {track.trackNumber}</h2>
            </div>

            <div className="track-info">
              <div className="info-item">
                <label>Статус:</label>
                <div
                  className="status-badge"
                  style={{ backgroundColor: getStatusColor(track.currentStatus) }}
                >
                  {track.currentStatus || 'Неизвестен'}
                </div>
              </div>

              {track.price && (
                <div className="info-item">
                  <label>Стоимость:</label>
                  <span>{track.price} тг</span>
                </div>
              )}

              {track.weight && (
                <div className="info-item">
                  <label>Вес:</label>
                  <span>{track.weight} кг</span>
                </div>
              )}

              <div className="info-item">
                <label>Дата создания:</label>
                <span>{formatDate(track.createdAt)}</span>
              </div>
            </div>

            <div className="track-history">
              <div
                className="history-header"
                onClick={() => setExpandedHistory(!expandedHistory)}
              >
                <h3>📋 История статусов ({track.history?.length || 0})</h3>
                <span className={`expand-icon ${expandedHistory ? 'expanded' : ''}`}>▼</span>
              </div>

              {expandedHistory && (
                <div className="history-list">
                  {track.history && track.history.length > 0 ? (
                    track.history.map((item, index) => (
                      <div key={index} className="history-item">
                        <div className="history-date">{formatDate(item.date)}</div>
                        <div className="history-status">
                          {item.status?.statusText || item.status || 'Статус неизвестен'}
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="no-history">История отсутствует</p>
                  )}
                </div>
              )}
            </div>

            <button
              className="btn-new-search"
              onClick={() => {
                setTrackNumber('');
                setPhone('');
                setTrack(null);
              }}
            >
              🔄 Новый поиск
            </button>
          </div>
        )}

        {!track && !error && !loading && (
          <div className="empty-state">
            <p>📪 Результаты поиска появятся здесь</p>
          </div>
        )}
      </div>

      <div className="tracking-help">
        <h3>❓ Как отследить посылку?</h3>
        <ol>
          <li>Введите номер вашей посылки в поле выше</li>
          <li>Опционально укажите номер телефона</li>
          <li>Нажмите кнопку "Найти трек"</li>
          <li>Вы увидите текущий статус и полную историю</li>
        </ol>
      </div>
    </div>
  );
}
